/**
 * useEocAttribution.ts
 *
 * Fetches per-sourceContent EOC (End of Call) attribution stats.
 * Keyed by sourceContent.trim().toLowerCase() → { closes, cashCollected }
 *
 * Matching against ad names uses fuzzy logic:
 *   1. Exact key match
 *   2. Ad name contains the EOC key (e.g. "vid2" inside "Marsh_VID_..._vid2")
 *   3. EOC key contains the ad name
 */

import { useState, useEffect } from "react";

export interface EocCreativeRow {
  closes:        number;
  cashCollected: number;
}

/** key = sourceContent.trim().toLowerCase() */
export type EocAttributionMap = Map<string, EocCreativeRow>;

export interface EocTotals {
  closes:        number;
  cashCollected: number;
}

export interface EocAttribution {
  data:    EocAttributionMap;
  totals:  EocTotals;
  loading: boolean;
  error:   string | null;
}

export function useEocAttribution(startDate?: string, endDate?: string): EocAttribution {
  const [data, setData]       = useState<EocAttributionMap>(new Map());
  const [totals, setTotals]   = useState<EocTotals>({ closes: 0, cashCollected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const params = new URLSearchParams();
        if (startDate) params.set("startDate", startDate);
        if (endDate)   params.set("endDate", endDate);
        const qs = params.toString();
        const resp = await fetch(`/api/eoc-creative-attribution${qs ? `?${qs}` : ""}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json() as {
          success: boolean;
          data: Record<string, EocCreativeRow>;
          totals: EocTotals;
        };
        if (!cancelled) {
          const map = new Map<string, EocCreativeRow>();
          for (const [key, val] of Object.entries(json.data ?? {})) {
            map.set(key, val);
          }
          setData(map);
          setTotals(json.totals ?? { closes: 0, cashCollected: 0 });
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  return { data, totals, loading, error };
}

/**
 * Look up EOC attribution for a given ad name.
 * Tries exact, then ad-contains-key, then key-contains-ad.
 * Returns the best match (longest matching key wins ties).
 */
export function lookupEocAttribution(
  adName: string,
  map: EocAttributionMap,
): EocCreativeRow | null {
  if (map.size === 0) return null;
  const needle = adName.toLowerCase().trim();

  // 1. Exact match
  const exact = map.get(needle);
  if (exact) return exact;

  // 2. Find best partial match — ad name contains key, or key contains ad name
  let best: EocCreativeRow | null = null;
  let bestLen = 0;

  for (const [key, val] of map) {
    if (key.length < 3) continue; // skip very short keys to avoid noise
    if (needle.includes(key) || key.includes(needle)) {
      if (key.length > bestLen) {
        bestLen = key.length;
        best = val;
      }
    }
  }

  return best;
}
